import { Type } from '@angular/core';
import { formatNumber } from '@angular/common';

import { CirculationFilterApiModel } from '../../../api/targeting/model/targeting-search.api.model';
import { FilterTypesEnum } from '../../Filter/enums/filters-types.enum';
import { AbstractFilterTypeComponent } from '../../Filter/models/abstract-filter-type.component';
import { CustomFilterTypeModel } from '../../Filter/models/custom-filter-type.model';
import { ChipModel } from '../../Forms/models/chip.model';
import { DropdownOptionModel } from '../../Forms/models/dropdown-option.model';
import type { LocalizedEnum } from '../../../api/journodex/model/localized-enum';

export class ReachFilterModel extends CustomFilterTypeModel<CirculationFilterApiModel> {
    readonly key: FilterTypesEnum = FilterTypesEnum.custom;

    value: CirculationFilterApiModel;
    currentValue: CirculationFilterApiModel;

    private circulationTypes: LocalizedEnum[];

    circulationTypeOptions: DropdownOptionModel[];
    dropdownSelection: DropdownOptionModel;


    constructor(
        id: string,
        label: string,
        component: Type<AbstractFilterTypeComponent>,
        section?: string
    ) {
        super(id, label, component, section);
        this.setValue(ReachFilterModel.createBlankFilter());
    }

    static createBlankFilter(): CirculationFilterApiModel {
        return {
            range: {},
            type: null
        };
    }

    setCirculationTypes(circulationTypes: LocalizedEnum[]) {
        if (circulationTypes) {
            this.circulationTypes = circulationTypes;
            this.circulationTypeOptions = circulationTypes.map(x => {
                return new DropdownOptionModel(x.displayString, x.enumValue);
            });
            if (this.value.type) {
                this.dropdownSelection = this.circulationTypeOptions.find(x => x.value === this.value.type);
            } else {
                this.dropdownSelection = this.circulationTypeOptions[0];
                this.value.type = this.dropdownSelection.value;
            }
        }
    }

    private makeChip(val: CirculationFilterApiModel): ChipModel|null {
        if ((isNaN(val.range.min) && isNaN(val.range.max)) || !this.circulationTypes) {
            return null;
        } else {
            if (isNaN(val.range.min)) {
                return {
                    label: 'Common.filter.range.lessThan',
                    labelArgs: {
                        name: this.circulationTypes.find(x => x.enumValue === val.type)?.displayString,
                        max: formatNumber(val.range.max, 'en')
                    }
                };
            } else if (isNaN(val.range.max)) {
                return {
                    label: 'Common.filter.range.greaterThan',
                    labelArgs: {
                        name: this.circulationTypes.find(x => x.enumValue === val.type)?.displayString,
                        min: formatNumber(val.range.min, 'en')
                    }
                };
            }
            return {
                label: 'Common.filter.range.minToMax',
                labelArgs: {
                    name: this.circulationTypes.find(x => x.enumValue === val.type)?.displayString,
                    min: formatNumber(val.range.min, 'en'),
                    max: formatNumber(val.range.max, 'en')
                }
            };
        }
    }

    setValue(value: CirculationFilterApiModel): void {
        this.value = value;
        this.chips = [];
        const chip = this.makeChip(this.value);
        if (chip) {
            this.chips.push(chip);
        }
        this.cancelChanges();
    }


    getValue(): CirculationFilterApiModel {
        return this.value;
    }

    removeChip(chip: ChipModel): void {
        this.setValue(ReachFilterModel.createBlankFilter());
    }

    clear(): void {
        this.setValue(ReachFilterModel.createBlankFilter());
    }

    hasValueSet(): boolean {
        return !!this.value?.range?.min || !!this.value?.range?.max;
    }

    setCurrentValue(value: CirculationFilterApiModel): void {
        this.currentValue = value;
        this.currentChips = [];
        const chip = this.makeChip(this.currentValue);
        if (chip) {
            this.currentChips.push(chip);
        }
    }

    getCurrentValue(): CirculationFilterApiModel {
        return this.currentValue;
    }

    removeCurrentChip(chip: ChipModel): void {
        this.setCurrentValue(ReachFilterModel.createBlankFilter());
    }

    cancelChanges(): void {
        this.setCurrentValue({
            range: {
                min: this.value.range.min,
                max: this.value.range.max
            },
            type: this.value.type
        });
    }
}
